import React, { useEffect, useMemo, useState } from 'react';
import { api, btnDanger, btnPrimary, btnSecondary, InlineError, input, labelCls, Modal, Skeleton, useToast } from '../ui/Kit';
import { PrivacyHint } from './common';
import { browserZone, inZone, ZONES } from './AlexDialogs';

// Interview rounds (bundle 5a): Schedule round N (date, time, the candidate's time zone, the panel from the plan —
// editable within the Interview pool —, a meeting link, a note to the candidate), Reschedule and Cancel (reason).
// Each sends calendar invites (.ics) to the candidate and every interviewer.
// Part C — on-site: one of the company's offices (or a typed address), in the office's time zone, room / floor, host
// (default: the lead), arrival (default 10 minutes early); each interviewer in person or remote (a link only if remote).

export const officeText = (o) => (o.label ? `${o.label} — ${o.address || [o.city, o.state].filter(Boolean).join(', ')}` : (o.address || [o.city, o.state, o.country].filter(Boolean).join(', ')));

export function RoundScheduleDialog({ appId, onClose, onDone }) {
  const toast = useToast();
  const [nx, setNx] = useState(null);
  const [when, setWhen] = useState('');
  const [zone, setZone] = useState('');
  const [panel, setPanel] = useState([]);
  const [lead, setLead] = useState(null);
  const [link, setLink] = useState('');
  const [typed, setTyped] = useState(false);          // the recruiter's own link wins over the lead's saved one
  const [note, setNote] = useState('');
  const [where, setWhere] = useState('');             // on-site: an office from the list, or typed
  const [room, setRoom] = useState('');
  const [host, setHost] = useState(null);
  const [arrival, setArrival] = useState(10);
  const [seat, setSeat] = useState({});               // {user_id: 'in_person' | 'remote'}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    api(`/api/applications/${appId}/rounds/next`).then((d) => {
      setNx(d); setZone(d.timezone || browserZone());
      const ids = (d.plan && d.plan.interviewer_ids) || [];
      setPanel(ids); setLead((d.plan && d.plan.lead_user_id) || ids[0] || null);
      if (d.plan && d.plan.mode === 'onsite') {
        const first = (d.offices || [])[0];
        setWhere(d.plan.location || (first ? officeText(first) : ''));
        if (!d.plan.location && first && first.timezone) setZone(first.timezone);
      }
    }).catch((e) => setError(e.message));
  }, [appId]);
  const zones = useMemo(() => [...new Set([zone, browserZone(), ...ZONES].filter(Boolean))], [zone]);
  const video = !nx || !nx.plan || nx.plan.mode === 'video';
  const onsite = !!(nx && nx.plan && nx.plan.mode === 'onsite');
  const office = onsite ? (nx.offices || []).find((o) => officeText(o) === where) : null;
  // Part E: in person only for people whose office is this round's office (an unlisted, typed address has no office to compare)
  const poolById = Object.fromEntries((nx ? nx.pool || [] : []).map((p) => [p.id, p]));
  const sameOffice = (id) => !office || (poolById[id] && poolById[id].office_location_id === office.id);
  const attendance = Object.fromEntries(panel.map((id) => [id, seat[id] || (sameOffice(id) ? 'in_person' : 'remote')]));
  const someoneRemote = onsite && panel.some((id) => attendance[id] === 'remote');
  const pickWhere = (v) => {
    setWhere(v);
    const o = (nx.offices || []).find((x) => officeText(x) === v);
    if (o && o.timezone) setZone(o.timezone);           // the office's time zone; everyone sees it in their own
  };
  useEffect(() => {
    if (typed || !nx || !(video || someoneRemote)) return;
    const p = (nx.pool || []).find((x) => x.id === lead);
    setLink((p && p.meeting_link) || '');
  }, [lead, nx, typed, video, someoneRemote]);
  const flip = (id) => {
    const next = panel.includes(id) ? panel.filter((x) => x !== id) : [...panel, id];
    setPanel(next);
    if (!next.includes(lead)) setLead(next[0] || null);
  };
  const go = async () => {
    setBusy(true); setError('');
    try {
      const r = await api(`/api/applications/${appId}/rounds`, { method: 'POST', body: { scheduled_datetime: when, timezone: zone, round_number: nx.round_number,
        interviewer_ids: panel, lead_user_id: lead, meeting_link: (video || someoneRemote) ? (link.trim() || null) : null, note: note.trim() || null,
        ...(onsite ? { office_location_id: office ? office.id : null, location: office ? null : where.trim(), room: room.trim() || null,
          host_user_id: host || lead, arrival_minutes: Number(arrival), attendance } : {}) } });
      toast.show(`Round ${r.round_number} scheduled — invites sent`); onDone && onDone(r);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  const plan = nx && nx.plan;
  return (
    <Modal open title={nx && nx.round_number ? `Schedule round ${nx.round_number}${plan ? ` · ${plan.name}` : ''}` : 'Schedule round'} onClose={onClose} width="max-w-lg"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={busy || !nx || !nx.can_schedule || !when || !panel.length || (onsite && (!where.trim() || (someoneRemote && !link.trim())))} onClick={go} data-testid="round-schedule-confirm">{busy ? 'Scheduling…' : 'Schedule'}</button></>}>
      {!nx && !error ? <div className="space-y-2" aria-busy="true"><Skeleton /><Skeleton /></div> : nx && <>
        {!nx.can_schedule && <p className="text-sm text-[#92400E] mb-3" data-testid="round-why-not">{nx.why_not}</p>}
        <p className="text-sm text-[#334155] mb-3" data-testid="round-summary">{nx.candidate} · {nx.job_title}{plan ? ` · ${plan.duration_minutes} min · ${({ video: 'Video', phone: 'Phone', onsite: 'On-site' })[plan.mode]}${plan.location ? ` (${plan.location})` : ''}` : ''}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Date and time</span>
            <input type="datetime-local" className={input} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="round-when" /></label>
          <label className="block"><span className={labelCls}>{onsite ? 'Office time zone' : 'Candidate’s time zone'}</span>
            <select className={input} value={zone} onChange={(e) => setZone(e.target.value)} data-testid="round-zone">{zones.map((z) => <option key={z} value={z}>{z}</option>)}</select></label>
        </div>
        <fieldset className="mt-3"><legend className={labelCls}>Interviewers (Interview permission)</legend>
          <div className="flex flex-wrap gap-1.5" data-testid="round-panel">
            {(nx.pool || []).map((p) => (
              <button key={p.id} type="button" aria-pressed={panel.includes(p.id)} onClick={() => flip(p.id)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${panel.includes(p.id) ? 'border-[#0066CC] bg-[#0066CC] text-white' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>{p.name}</button>))}
          </div></fieldset>
        {panel.length > 1 && <label className="block mt-3"><span className={labelCls}>Lead interviewer (sets Pass / Fail)</span>
          <select className={input} value={lead || ''} onChange={(e) => setLead(Number(e.target.value))} data-testid="round-lead">
            {(nx.pool || []).filter((p) => panel.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>}
        {onsite && <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="round-onsite-fields">
          <label className="block sm:col-span-2"><span className={labelCls}>Location * (an office, or type an address)</span>
            <input className={input} list="round-offices" value={where} onChange={(e) => pickWhere(e.target.value)} data-testid="round-location" />
            <datalist id="round-offices">{(nx.offices || []).map((o) => <option key={o.id} value={officeText(o)} />)}</datalist></label>
          <label className="block"><span className={labelCls}>Room / floor (optional)</span>
            <input className={input} value={room} maxLength={120} onChange={(e) => setRoom(e.target.value)} data-testid="round-room" /></label>
          <label className="block"><span className={labelCls}>Arrive early (minutes)</span>
            <input type="number" min={0} max={60} className={input} value={arrival} onChange={(e) => setArrival(e.target.value)} data-testid="round-arrival" /></label>
          <label className="block sm:col-span-2"><span className={labelCls}>Host (meets the candidate)</span>
            <select className={input} value={host || lead || ''} onChange={(e) => setHost(Number(e.target.value))} data-testid="round-host">
              {(nx.pool || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
          <fieldset className="sm:col-span-2"><legend className={labelCls}>Each interviewer</legend>
            <ul className="space-y-1" data-testid="round-attendance">{(nx.pool || []).filter((p) => panel.includes(p.id)).map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-sm"><span>{p.name}
                {!sameOffice(p.id) && <span className="block text-xs text-[#B45309]" data-testid={`seat-note-${p.id}`}>{p.work === 'Remote' ? 'works remotely' : p.work ? `works from ${p.work}` : 'no office set'}</span>}</span>
                <span className="flex gap-1">{[['in_person', 'In person'], ['remote', 'Remote']].map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={attendance[p.id] === k} onClick={() => setSeat({ ...seat, [p.id]: k })} data-testid={`seat-${p.id}-${k}`}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${attendance[p.id] === k ? 'border-[#0066CC] bg-[#0066CC] text-white' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>{l}</button>))}</span></li>))}</ul>
          </fieldset>
        </div>}
        {(video || someoneRemote) && <label className="block mt-3"><span className={labelCls}>{nx.meeting_provider ? `${nx.meeting_provider} link` : 'Meeting link'} {someoneRemote ? '* (for the remote interviewers only)' : '(optional — pre-filled from the lead’s saved link)'}</span>
          <input className={input} value={link} placeholder="https://…" onChange={(e) => { setTyped(true); setLink(e.target.value); }} data-testid="round-link" /></label>}
        <label className="block mt-3"><span className={labelCls}>Note to the candidate (optional)</span>
          <textarea className={input} rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} data-testid="round-note" /><PrivacyHint /></label>
      </>}
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export function RoundRescheduleDialog({ round, onClose, onDone }) {
  const toast = useToast();
  const [when, setWhen] = useState('');
  const [error, setError] = useState('');
  const go = async () => {
    try { const r = await api(`/api/interview-rounds/${round.id}/reschedule`, { method: 'POST', body: { scheduled_datetime: when } }); toast.show('Rescheduled — updated invites sent'); onDone && onDone(r); }
    catch (e) { setError(e.message); }
  };
  return (
    <Modal open title={`Reschedule round ${round.round_number}`} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button><button className={btnPrimary} disabled={!when} onClick={go} data-testid="round-reschedule-confirm">Reschedule</button></>}>
      <p className="text-sm text-[#64748B] mb-3">Now: {inZone(round.scheduled_start, round.timezone)}</p>
      <label className="block"><span className={labelCls}>New date and time ({round.timezone})</span>
        <input type="datetime-local" className={input} value={when} onChange={(e) => setWhen(e.target.value)} data-testid="round-new-when" /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export function RoundCancelDialog({ round, onClose, onDone }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const go = async () => {
    try { const r = await api(`/api/interview-rounds/${round.id}/cancel`, { method: 'POST', body: { reason: reason.trim() } }); toast.show('Round cancelled — back to “to schedule”'); onDone && onDone(r); }
    catch (e) { setError(e.message); }
  };
  return (
    <Modal open title={`Cancel round ${round.round_number}`} onClose={onClose} width="max-w-md"
      footer={<><button className={btnSecondary} onClick={onClose}>Keep it</button><button className={btnDanger} disabled={reason.trim().length < 3} onClick={go} data-testid="round-cancel-confirm">Cancel the round</button></>}>
      <p className="text-sm text-[#334155]">The candidate and the interviewers get a calendar cancellation; the candidate goes back to “to schedule”.</p>
      <label className="block mt-3"><span className={labelCls}>Reason (recorded)</span>
        <textarea className={input} rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="round-cancel-reason" /></label>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}
